import { compositeOver, readableColor, contrastRatio, deltaE, relLum } from './theme/contrast';
import { createButtonColors, type ButtonColors } from './theme/button-colors';
import { atLightness, closestColor, createBrandPalette } from './theme/brand-palette';
import { converter } from 'culori';

/** Independent answer fills and labels, including persistent selection. */
export interface ChoiceColors {
  background: string;
  foreground: string;
  hover: string;
  hoverForeground: string;
  selected: string;
  selectedForeground: string;
  selectedHover: string;
  selectedHoverForeground: string;
}

/** Keep existing base washes; select the closest vivid brand fill with distinct states. */
function createChoiceColors(palette: NonNullable<ReturnType<typeof createBrandPalette>>, buttons: ButtonColors, dark: boolean): ChoiceColors {
  const { accent, ink } = palette;
  const page = dark ? '#0a0a0d' : '#ffffff';
  const foreground = dark ? '#f4f4f7' : '#1a1a22';
  const background = readableColor(compositeOver(accent, 0.14, dark ? '#28282e' : '#e9e9ee'), dark ? '#ffffff' : '#000000', [page], 1.3);
  const toOklch = converter('oklch');
  const lightness = toOklch(background)!.l;
  const hover = closestColor(background, color => contrastRatio(foreground, color) >= 4.5 && deltaE(background, color) >= 4
    && (dark ? toOklch(color)!.l > lightness : toOklch(color)!.l < lightness)) ?? background;
  const selectionConstraint = (color: string) => contrastRatio(ink, color) >= 4.5 && deltaE(background, color) >= 10
    && deltaE(hover, color) >= 6 && deltaE(page, color) >= 10
    && [buttons.background, buttons.hover, buttons.active].every(action => deltaE(action, color) >= 12);
  const selected = closestColor(accent, selectionConstraint) ?? readableColor(accent, '#ffffff', [ink]);
  const selectedHover = closestColor(selected, color => selectionConstraint(color) && deltaE(selected, color) >= 4) ?? selected;
  return { background, foreground, hover, hoverForeground: foreground,
    selected, selectedForeground: ink, selectedHover, selectedHoverForeground: ink };
}

/** Colors derived from a space's accent for respondent controls and decoration. */
export interface ConversationPalette {
  accent: string;
  button: string;
  foreground: string;
  hover: string;
  hoverForeground: string;
  active: string;
  activeForeground: string;
  soft: string;
  textOnLight: string;
  wash: string;
  washHover: string;
  darkButton: ButtonColors;
  choicesLight: ChoiceColors;
  choicesDark: ChoiceColors;
  mistLight: string[];
  mistDark: string[];
}

/** Validate persisted hex colors and derive accessible light and dark variants. */
export function createConversationPalette(value?: string | null): ConversationPalette | null {
  const colors = createButtonColors(value);
  if (!colors) return null;
  const { accent, background: button, foreground, hover, hoverForeground, active, activeForeground } = colors;
  const wash = compositeOver(accent, 0.06, '#f6f6f8');
  const washHover = compositeOver(accent, 0.12, '#f6f6f8');
  const brand = createBrandPalette(accent)!;
  const mistCore = closestColor(atLightness(accent, 0.795), color => contrastRatio(color, '#ffffff') >= 1.8 && relLum(color) >= 0.45)!;
  return {
    accent,
    button,
    foreground,
    hover,
    hoverForeground,
    active,
    activeForeground,
    soft: readableColor(accent, '#ffffff', ['#18181c', '#212126', '#2a2a30']),
    textOnLight: readableColor(accent, '#000000', ['#ffffff', wash, washHover]),
    wash,
    washHover,
    darkButton: colors,
    choicesLight: createChoiceColors(brand, colors, false),
    choicesDark: createChoiceColors(brand, colors, true),
    // The shader already fades dye over the page. Keep enough color in these
    // stops to survive that fade, while lightness keeps dark accents out of ink.
    mistLight: [atLightness(accent, 0.86), mistCore, atLightness(accent, 0.9)],
    mistDark: [0.2, 0.4, 0.6].map(alpha => compositeOver('#ffffff', alpha, accent)),
  };
}
